import React, { useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "~/components/Button";
import { Input } from "~/components/Controls/Input";
import { SelectInput } from "~/components/Controls/Select";
import { useConfirm, useMessage } from "~/components/Dialogs";
import { IntentText } from "~/components/Form";
import { Progress } from "~/components/Progress";
import { TableRowActions, TableSort } from "~/components/Table";
import { Intent } from "~/lib/intent";
import { sortByString } from "~/lib/sort";
import {
  createExternalBuyerP2P,
  deleteExternalBuyerP2P,
  uploadExternalBuyerP2P,
  updateExternalBuyerP2P,
} from "~/modules/Dictionary/api";
import { DictionaryUsageLinks } from "~/modules/Dictionary/components/DictionaryUsageLinks";
import { IExternalBuyerP2P, IExternalBuyerP2PUploadResult } from "~/modules/Dictionary/models/ExternalBuyerP2P";
import {
  EXTERNAL_BUYERS_P2P_QUERY_KEY,
  useDspConnectionTypesQuery,
  useExternalBuyersP2PQuery,
} from "~/modules/Dictionary/queries";
import { SelectDspConnectionType } from "~/modules/Clients/components/SelectDspConnectionType";
import style from "./ExternalBuyersTab.module.scss";

const getErrorMessage = (error: any): string => error?.response?.data?.message || error?.message || "Unknown error";

type FormMode = "idle" | "create" | "edit";

type UpdatePayload = {
  id: number;
  name: string;
  buyerId: string | null;
  dspConnectionType: string | null;
};

const normalizeExternalBuyersCsv = (raw: string) => {
  let changed = false;
  const normalized = raw.split(/\r?\n/).map((line) => {
    if (!line || line.includes('"')) {
      return line;
    }
    const parts = line.split(",");
    if (parts.length <= 2) {
      return line;
    }
    const [buyerId, ...rest] = parts;
    const name = rest.join(",");
    changed = true;
    return `${buyerId},"${name.replace(/"/g, '""')}"`;
  });
  return { text: normalized.join("\n"), changed };
};

export const ExternalBuyersP2PUploadControls = () => {
  const queryClient = useQueryClient();
  const message = useMessage();

  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [uploadDspConnectionType, setUploadDspConnectionType] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: (payload: { file: File; dspConnectionType: string }) =>
      uploadExternalBuyerP2P(payload.file, payload.dspConnectionType),
    onSuccess: (result: IExternalBuyerP2PUploadResult) => {
      message(`Upload complete. Created: ${result.created}, skipped: ${result.skipped}`, {
        intent: Intent.SUCCESS,
        icon: <i className="fa fa-check" />,
      });
      queryClient.invalidateQueries({ queryKey: EXTERNAL_BUYERS_P2P_QUERY_KEY });
    },
    onError: (e: any) => {
      message(getErrorMessage(e), { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
    },
  });

  const isUploading = uploadMutation.isPending;

  const handleUploadClick = () => {
    if (!uploadDspConnectionType?.trim()) {
      message("Select DSP connection type first.", {
        intent: Intent.DANGER,
        icon: <i className="fa fa-times" />,
      });
      return;
    }
    uploadInputRef.current?.click();
  };

  const handleUploadChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const dspValue = uploadDspConnectionType?.trim();
    if (!dspValue) {
      message("Select DSP connection type first.", {
        intent: Intent.DANGER,
        icon: <i className="fa fa-times" />,
      });
      event.target.value = "";
      return;
    }
    try {
      const raw = await file.text();
      const { text, changed } = normalizeExternalBuyersCsv(raw);
      const uploadFile = changed ? new File([text], file.name, { type: file.type || "text/csv" }) : file;
      uploadMutation.mutate({ file: uploadFile, dspConnectionType: dspValue });
    } catch (e: any) {
      message(getErrorMessage(e), { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
    } finally {
      event.target.value = "";
    }
  };

  return (
    <div className={style.uploadHelpWrap}>
      <div className={style.uploadControls}>
        <input
          ref={uploadInputRef}
          type="file"
          accept=".csv,text/csv"
          className={style.uploadInput}
          onChange={handleUploadChange}
        />
        <SelectDspConnectionType
          className={style.uploadSelect}
          placeholder="DSP connection type"
          value={uploadDspConnectionType}
          onChange={setUploadDspConnectionType}
          disabled={isUploading}
          clearable
        />
        <Button
          responsive
          intent={Intent.WARNING}
          disabled={isUploading || !uploadDspConnectionType?.trim()}
          onClick={handleUploadClick}
          leftIcon={<i className="fa fa-upload" />}
        >
          Upload CSV
        </Button>
      </div>
      <div className={style.uploadHelp}>
        Select DSP connection type first. CSV format: <code>buyerId,name</code>.
      </div>
    </div>
  );
};

export const ExternalBuyersP2PTab = () => {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const message = useMessage();

  const { data: items = [], isPending, isFetching, isError, error, refetch } = useExternalBuyersP2PQuery();
  const { data: dspConnectionTypes = [] } = useDspConnectionTypesQuery();

  const [mode, setMode] = useState<FormMode>("idle");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [buyerId, setBuyerId] = useState("");
  const [dspConnectionType, setDspConnectionType] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dspFilter, setDspFilter] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (payload: { name: string; buyerId: string | null; dspConnectionType: string | null }) =>
      createExternalBuyerP2P(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: EXTERNAL_BUYERS_P2P_QUERY_KEY });
      setName("");
      setBuyerId("");
      setDspConnectionType(null);
      setFormError(null);
      setEditingId(null);
      setMode("idle");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: UpdatePayload) =>
      updateExternalBuyerP2P(payload.id, {
        name: payload.name,
        buyerId: payload.buyerId,
        dspConnectionType: payload.dspConnectionType,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: EXTERNAL_BUYERS_P2P_QUERY_KEY });
      setName("");
      setBuyerId("");
      setDspConnectionType(null);
      setFormError(null);
      setEditingId(null);
      setMode("idle");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteExternalBuyerP2P(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: EXTERNAL_BUYERS_P2P_QUERY_KEY });
    },
  });

  const normalizedItems = useMemo(() => items ?? [], [items]);
  const dspLabelByName = useMemo(() => {
    const map = new Map<string, string>();
    dspConnectionTypes.forEach((t) => {
      if (t?.name) {
        map.set(t.name, t.label || t.name);
      }
    });
    return map;
  }, [dspConnectionTypes]);
  const dspFilterItems = useMemo(() => {
    const values = new Set<string>();
    normalizedItems.forEach((item) => {
      if (item.dspConnectionType) {
        values.add(item.dspConnectionType);
      }
    });
    return [...values]
      .map((value) => ({
        value,
        label: dspLabelByName.get(value) ?? value,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [normalizedItems, dspLabelByName]);
  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return normalizedItems.filter((item) => {
      if (dspFilter && item.dspConnectionType !== dspFilter) {
        return false;
      }
      if (!query) {
        return true;
      }
      const buyerIdValue = (item.buyerId ?? "").toLowerCase();
      const nameValue = (item.name ?? "").toLowerCase();
      const dspValue = (item.dspConnectionType ?? "").toLowerCase();
      return buyerIdValue.includes(query) || nameValue.includes(query) || dspValue.includes(query);
    });
  }, [normalizedItems, dspFilter, searchQuery]);
  const isBusy =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;
  const isFormOpen = mode !== "idle";
  const isEditing = mode === "edit";
  const canSubmit =
    name.trim().length > 0 && buyerId.trim().length > 0 && !!dspConnectionType?.trim();

  const handleOpenAdd = () => {
    setFormError(null);
    setName("");
    setBuyerId("");
    setDspConnectionType(null);
    setEditingId(null);
    setMode("create");
  };

  const handleEdit = (item: IExternalBuyerP2P) => {
    setFormError(null);
    setName(item.name ?? "");
    setBuyerId(item.buyerId ?? "");
    setDspConnectionType(item.dspConnectionType ?? null);
    setEditingId(item.id);
    setMode("edit");
  };

  const handleCancel = () => {
    setFormError(null);
    setName("");
    setBuyerId("");
    setDspConnectionType(null);
    setEditingId(null);
    setMode("idle");
  };

  const handleSave = () => {
    setFormError(null);
    const trimmedName = name.trim();
    const trimmedBuyerId = buyerId.trim();
    const trimmedDspConnectionType = dspConnectionType?.trim() ?? "";
    const payloadBuyerId = trimmedBuyerId.length ? trimmedBuyerId : null;
    const payloadDspConnectionType = trimmedDspConnectionType.length ? trimmedDspConnectionType : null;
    if (!trimmedName || !trimmedBuyerId || !payloadDspConnectionType) {
      return;
    }

    if (isEditing && editingId !== null) {
      updateMutation.mutate(
        { id: editingId, name: trimmedName, buyerId: payloadBuyerId, dspConnectionType: payloadDspConnectionType },
        {
          onError: (e: any) => {
            const msg = getErrorMessage(e);
            setFormError(msg);
            message(msg, { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
          },
        }
      );
      return;
    }

    createMutation.mutate(
      { name: trimmedName, buyerId: payloadBuyerId, dspConnectionType: payloadDspConnectionType },
      {
        onError: (e: any) => {
          const msg = getErrorMessage(e);
          setFormError(msg);
          message(msg, { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
        },
      }
    );
  };

  const handleDelete = (item: IExternalBuyerP2P) =>
    confirm(`Delete external buyer "${item.name}"?`, {
      icon: <i className="fa fa-question" />,
      intent: Intent.DANGER,
      onOk: () => {
        deleteMutation.mutate(item.id, {
          onError: (e: any) => {
            message(getErrorMessage(e), { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
          },
        });
      },
    });

  return (
    <div>
        <div className={style.toolbar}>
          <div className={style.toolbarLeft}>
            {isFormOpen && (
            <div className={style.formRowThree}>
              <Input
                className={style.field}
                placeholder="Buyer Id"
                value={buyerId}
                onChange={setBuyerId}
                disabled={isBusy}
                autoFocus
              />
              <Input
                className={style.field}
                placeholder="Name"
                value={name}
                onChange={setName}
                disabled={isBusy}
              />
              <SelectDspConnectionType
                className={style.field}
                placeholder="Type"
                value={dspConnectionType}
                onChange={setDspConnectionType}
                disabled={isBusy}
                clearable
              />
            </div>
          )}
          {!isFormOpen && (
            <div className={style.usageNote}>
              <div className={style.usageText}>
                <DictionaryUsageLinks
                  links={[
                    {
                      label: "Pivot",
                      href: "https://pivot.bidmachine.io/pivot/i/9df4681d23a3991fcb/2_Impressions",
                      external: true,
                    },
                  ]}
                />
              </div>
              <div className={style.usageControls}>
                {!!dspFilterItems.length && (
                  <SelectInput
                    className={style.usageFilter}
                    items={dspFilterItems}
                    value={dspFilter}
                    onChange={setDspFilter}
                    placeholder="Filter by DSP"
                    clearable
                    searchable
                  />
                )}
                <Input
                  className={style.usageSearch}
                  placeholder="Search by Buyer Id or Name"
                  value={searchQuery}
                  onChange={setSearchQuery}
                  rightIcon={<i className="fa fa-search input-search-icon" />}
                />
              </div>
            </div>
          )}
        </div>
        <div className={style.toolbarRight}>
          {!isFormOpen ? (
            <Button
              responsive
              intent={Intent.PRIMARY}
              disabled={isBusy}
              onClick={handleOpenAdd}
              leftIcon={<i className="fa fa-plus" />}
            >
              Add
            </Button>
          ) : (
            <>
              <Button
                responsive
                intent={Intent.PRIMARY}
                disabled={!canSubmit || isBusy}
                onClick={handleSave}
                leftIcon={<i className="fa fa-check" />}
              >
                {isEditing ? "Update" : "Save"}
              </Button>
              <Button
                responsive
                disabled={isBusy}
                onClick={handleCancel}
                leftIcon={<i className="fa fa-times" />}
              >
                Cancel
              </Button>
            </>
          )}
          <Button
            responsive
            intent={Intent.SUCCESS}
            disabled={isFetching}
            onClick={() => refetch()}
            leftIcon={<i className="fa fa-refresh" />}
          >
            Reload
          </Button>
        </div>
      </div>

      {isFormOpen && formError && <IntentText className={style.error} intent={Intent.DANGER}>{formError}</IntentText>}
      {isError && <IntentText className={style.error} intent={Intent.DANGER}>{getErrorMessage(error)}</IntentText>}

      <div className={style.tableWrap}>
        {isPending && filteredItems.length === 0 && <Progress message="Loading..." />}
        {!isPending && filteredItems.length === 0 && (
          <div className={style.emptyState}>No external buyers (P2P).</div>
        )}
        {filteredItems.length > 0 && (
          <TableSort
            records={filteredItems}
            header
            columns={[
              { header: "Buyer Id", field: "buyerId", sort: sortByString },
              { header: "Name", field: "name", sort: sortByString },
              {
                header: "DSP connection type",
                field: "dspConnectionType",
                sort: sortByString,
                cellAttrs: { className: style.dspCell },
                cell: (item: IExternalBuyerP2P) =>
                  item.dspConnectionType
                    ? `${dspLabelByName.get(item.dspConnectionType) ?? item.dspConnectionType} (${item.dspConnectionType})`
                    : "",
              },
              {
                short: true,
                cell: (item: IExternalBuyerP2P) => (
                  <TableRowActions>
                    <Button
                      small
                      variant="menu"
                      intent={Intent.PRIMARY}
                      disabled={isBusy}
                      onClick={() => handleEdit(item)}
                      leftIcon={<i className="fa fa-edit" />}
                    >
                      Edit
                    </Button>
                    <Button
                      small
                      variant="menu"
                      intent={Intent.DANGER}
                      disabled={isBusy}
                      onClick={() => handleDelete(item)}
                      leftIcon={<i className="fa fa-trash" />}
                    >
                      Delete
                    </Button>
                  </TableRowActions>
                ),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
};
